// HSK-mode building blocks: level constraints, voice tones, and the persona
// builder that turns a form input into a full Persona object compatible with
// the rest of the app.

import type { CustomPersonaInput, HskLevel, Persona } from './types';

interface HskRule {
  label: string;
  constraint: string;
}

export const HSK_RULES: Record<HskLevel, HskRule> = {
  1: {
    label: 'HSK 1 (~500 words)',
    constraint:
      'STRICT: stay inside HSK 1 vocabulary only. If a more advanced word fits, REPHRASE using simpler words rather than reaching higher. NO 把/被/让. NO aspect particles 过/着. NO idioms. Forbidden examples at this level: 应该, 已经, 觉得, 但是 — replace with simpler equivalents (要, 了, 想, 不过). If unsure whether a word is HSK 1, use a simpler synonym. Reply: 1-2 short sentences, max 12 Chinese characters.',
  },
  2: {
    label: 'HSK 2 (~1,272 words)',
    constraint:
      'STRICT: stay inside HSK 1-2 vocabulary. If a more advanced word fits, REPHRASE rather than reach higher. Basic 了 and simple time expressions OK. Forbidden examples at this level: 应该, 必须, 然后, 因此 — replace with simpler equivalents (要, 得, 就, 所以). If unsure whether a word is HSK 1-2, use a simpler synonym. Reply: 1-2 sentences, max 16 Chinese characters.',
  },
  3: {
    label: 'HSK 3 (~2,245 words)',
    constraint:
      'STRICT: stay inside HSK 1-3 vocabulary. If a more advanced word fits, REPHRASE rather than reach higher. Aspect particles (了, 过) OK. NO 把/被. Forbidden examples at this level: 既然, 然而, 此, 即使, 尽管 — replace with simpler equivalents (因为, 但是, 这, 就算, 虽然). If unsure whether a word is HSK 1-3, use a simpler synonym. Reply: 1-2 sentences, max 22 Chinese characters.',
  },
  4: {
    label: 'HSK 4 (~3,245 words)',
    constraint:
      'STRICT: stay inside HSK 1-4 vocabulary. If a more advanced word fits, REPHRASE rather than reach higher. 把 and aspect particles OK. Some colloquialisms. Forbidden examples at this level: 倘若, 鉴于, 凡是, 倒是 — replace with simpler equivalents (如果, 因为, 所有, 反而). Avoid literary idioms and obscure chengyu. Reply: 1-3 sentences, max 28 Chinese characters.',
  },
  5: {
    label: 'HSK 5 (~4,316 words)',
    constraint:
      'STRICT: stay inside HSK 1-5 vocabulary. If a more advanced word fits, REPHRASE rather than reach into HSK 6+ or classical Chinese. Full grammar range. Common idioms and proverbs OK occasionally — avoid rare or literary chengyu. Reply: 1-3 sentences, max 35 Chinese characters.',
  },
};

export interface VoiceTone {
  id: string;
  label: string;
  desc: string;
}

export const VOICE_TONES: VoiceTone[] = [
  { id: 'warm', label: 'Friendly & warm', desc: 'Approachable, welcoming, makes the user feel comfortable.' },
  { id: 'patient', label: 'Patient & encouraging', desc: 'Speaks slowly, encourages every attempt, repeats things in different ways.' },
  { id: 'casual', label: 'Casual & playful', desc: 'Uses slang, jokes around, treats the user like an old friend.' },
  { id: 'direct', label: 'Direct & no-nonsense', desc: 'Says what they mean. No fluff. Slightly gruff but not unkind.' },
  { id: 'gentle', label: 'Gentle & soft-spoken', desc: 'Quiet register. Thoughtful pauses. Uses softening particles like 啊 / 呢 / 吧.' },
  { id: 'witty', label: 'Witty & sarcastic', desc: 'Dry humor. Light teasing. Wordplay when it lands.' },
  { id: 'professional', label: 'Serious & professional', desc: 'Polite formal register. Uses 您 with appropriate users. Measured language.' },
  { id: 'curious', label: 'Curious & enthusiastic', desc: 'Asks lots of questions. Excited about the topic. Energetic.' },
];

const CUSTOM_GRADIENTS: [string, string][] = [
  ['#FFE4B5', '#D97A3C'],
  ['#E5D4F2', '#7B4FA8'],
  ['#C8E6C9', '#4A7C59'],
  ['#FFD1DC', '#C73E5C'],
  ['#B8DDF0', '#3E7FA8'],
  ['#F0E0C8', '#8B6F47'],
  ['#D8C8E5', '#6B4F87'],
  ['#FFCAB5', '#C7472E'],
];

function pickGradient(seed: string): [string, string] {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff;
  return CUSTOM_GRADIENTS[Math.abs(h) % CUSTOM_GRADIENTS.length]!;
}

function buildSystemPrompt(input: CustomPersonaInput): string {
  const tone = VOICE_TONES.find((t) => t.id === input.tone) ?? VOICE_TONES[0]!;
  const rule = HSK_RULES[input.hskLevel];

  return `You are ${input.name}, a ${input.age}-year-old ${input.profession}.${input.interests ? ` Interests: ${input.interests}.` : ''}

PERSONALITY: ${tone.desc} You speak naturally in this register at all times.

${input.scenario ? `CONVERSATION CONTEXT: The user wants to practice this scenario with you: "${input.scenario}". Stay grounded in this scenario — naturally bring the conversation back to it if it drifts. Don't break character to acknowledge it as a "scenario" or "practice"; just live inside it.\n\n` : ''}SPEAKING STYLE — STRICT:
- ${rule.constraint}
- Stay in your tone (${tone.label.toLowerCase()}) consistently across every reply.
- The user is practicing reading and typing Chinese. Reply ONLY in Chinese characters in the hz field. Don't switch to English or pinyin in the hz field even if the user does.
- If the user types in pinyin or English, respond in Chinese as if they had typed Chinese.
- Don't lecture, don't explain grammar, don't break role to give learning advice.`;
}

const HSK_GREETINGS: Record<HskLevel, { hz: string; py: string; en: string }> = {
  1: { hz: '你好！', py: 'Nǐ hǎo!', en: 'Hello!' },
  2: { hz: '你好，很高兴认识你。', py: 'Nǐ hǎo, hěn gāoxìng rènshí nǐ.', en: 'Hi, nice to meet you.' },
  3: { hz: '你好。我们开始聊吧？', py: 'Nǐ hǎo. Wǒmen kāishǐ liáo ba?', en: 'Hi. Shall we start chatting?' },
  4: { hz: '你好啊。今天想聊什么？', py: 'Nǐ hǎo a. Jīntiān xiǎng liáo shénme?', en: 'Hi there. What do you want to talk about today?' },
  5: { hz: '你好。我准备好了，咱们聊聊吧。', py: 'Nǐ hǎo. Wǒ zhǔnbèi hǎo le, zánmen liáo liáo ba.', en: "Hi. I'm ready — let's chat." },
};

export function buildCustomPersona(input: CustomPersonaInput): Persona {
  const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const grad = pickGradient(id);
  const tone = VOICE_TONES.find((t) => t.id === input.tone) ?? VOICE_TONES[0]!;
  const greeting = HSK_GREETINGS[input.hskLevel];

  return {
    id,
    isCustom: true,
    customInput: input,
    customPortraitId: input.customPortraitId,
    name: input.name,
    pinyin: input.name,
    en: input.name,
    age: parseInt(input.age, 10) || 25,
    city: 'Custom',
    cityEn: 'Custom',
    occupation: input.profession,
    bio: input.scenario || input.interests || '',
    bioEn: input.scenario || input.interests || '',
    intro: `Custom HSK ${input.hskLevel} character. Tone: ${tone.label}.`,
    level: { tag: `HSK ${input.hskLevel}`, label: HSK_RULES[input.hskLevel].label, vibe: tone.label },
    topics: [],
    grad,
    accent: grad[1],
    systemPrompt: buildSystemPrompt(input),
    greeting: {
      ...greeting,
      words: [{ hz: '你好', py: 'nǐ hǎo', en: 'hello' }],
    },
  };
}
